import React, { useEffect } from 'react'
import { Space, Card } from 'antd'
import get from 'lodash.get'
import { useAuth } from '../../../hooks/useAuth'
import { useParams } from 'react-router-dom'
import FormContent from '../FormContent'
import { Payment, formOptions } from '../../../forms/blocks'
import { getFromKey } from '../utils/orderHelpers'

const Step5 = ({ values, errors, handleChange, orderDetails }) => {
  const formsPayment = [{ form: Payment, keys: '' }]
  const { user } = useAuth()
  const { storeId } = useParams()
  const currentStore = user?.stores?.find((store) => store.store === storeId)

  useEffect(() => {
    let total = 0

    for (const prod of values.orderProducts || []) {
      switch (prod.orderProductType) {
        case 'collection':
          total += get(prod, 'orderCollection.price', 0)
          break
        case 'custom': {
          // extra shirts
          const extraShirts = get(
            prod,
            `orderCustom.orderCustomShirt.extraShirts`,
            []
          ) || []
          extraShirts.forEach((item) => {
            total += get(item, 'priceNum', 0)
          })

          total += get(prod, 'orderCustom.price', 0)
          break
        }
        case 'adjustment':
          total += get(prod, 'orderAdjustment.price', 0)
          break
        case 'fixed_design':
          total += get(prod, 'orderFixedDesign.price', 0)
          break

        default:
          break
      }
    }
    total = parseFloat(total.toFixed(2))

    handleChange({
      target: {
        name: 'totalSpent',
        value: total,
      },
    })
    handleChange({
      target: {
        name: 'balance',
        value:
          total -
          get(values, 'discount', 0) -
          get(values, 'deposit', 0) -
          get(values, 'deposit2', 0),
      },
    })
  }, [])

  useEffect(() => {
    let val = get(values, 'totalSpent', 0)
    val = parseFloat((val - get(values, 'discount', 0)).toFixed(2))
    val = parseFloat(
      (val - get(values, 'deposit', 0) - get(values, 'deposit2', 0)).toFixed(2)
    )

    handleChange({
      target: {
        name: 'balance',
        value: val,
      },
    })
  }, [values.totalSpent, values.discount, values.deposit, values.deposit2])

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card title="Total Summary">
        <div
          style={{
            marginBottom: '16px',
            width: '50%',
            maxWidth: '50%',
            position: 'relative',
          }}
        >
          {(currentStore?.logo?.uri || !user?.franchiseStore) && (
            <img
              src={
                currentStore?.logo?.uri
                  ? `${process.env.REACT_APP_API_URL}/storage/store-logo/${currentStore.logo.uri}`
                  : '/images/atelier_logo.png'
              }
              alt="store logo"
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                maxHeight: '150px',
                objectFit: 'contain',
              }}
              loading="lazy"
            />
          )}
        </div>
        <table
          style={{
            width: '50%',
            borderCollapse: 'collapse',
            border: '1px solid #ddd',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f2f2f2',
                }}
              >
                Quantity
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f2f2f2',
                }}
              >
                Product
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f2f2f2',
                }}
              >
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {(values.orderProducts || []).flatMap((prod, prodIndex) => {
              const prodType = getFromKey(prod)
              
              // For shirt products with extra shirts, group by quality
              if (prodType === 'shirt' && prod.orderProductType === 'custom') {
                const mainQuality = get(prod, 'orderCustom.quality', '') || ''
                const mainPrice = get(prod, 'orderCustom.price', 0)
                const extraShirts = get(prod, 'orderCustom.orderCustomShirt.extraShirts', []) || []
                
                // Group shirts by quality
                const qualityGroups = {}
                
                // Add main shirt to groups (use empty string as key if no quality)
                const mainQualityKey = mainQuality || '(no quality)'
                if (!qualityGroups[mainQualityKey]) {
                  qualityGroups[mainQualityKey] = { quantity: 0, price: 0 }
                }
                qualityGroups[mainQualityKey].quantity += 1
                qualityGroups[mainQualityKey].price += mainPrice
                
                // Add extra shirts to groups
                extraShirts.forEach((extraShirt) => {
                  const extraQuality = get(extraShirt, 'price', '') || ''
                  const extraPrice = get(extraShirt, 'priceNum', 0)
                  const extraQualityKey = extraQuality || '(no quality)'
                  
                  if (!qualityGroups[extraQualityKey]) {
                    qualityGroups[extraQualityKey] = { quantity: 0, price: 0 }
                  }
                  qualityGroups[extraQualityKey].quantity += 1
                  qualityGroups[extraQualityKey].price += extraPrice
                })
                
                // If all shirts have the same quality, show one line
                const qualityKeys = Object.keys(qualityGroups)
                if (qualityKeys.length === 1) {
                  const singleQuality = qualityKeys[0]
                  const group = qualityGroups[singleQuality]
                  return [
                    <tr
                      key={`${prodIndex}-shirt`}
                      style={{
                        backgroundColor: prodIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                        borderBottom: '1px solid #ddd',
                      }}
                    >
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {group.quantity}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {formOptions[prodType]?.name ?? 'Shirt'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                        {group.price.toFixed(2)}
                      </td>
                    </tr>
                  ]
                }
                
                // If different qualities, show separate lines
                return qualityKeys.map((quality, qualityIndex) => {
                  const group = qualityGroups[quality]
                  const displayQuality = quality === '(no quality)' ? '' : quality
                  return (
                    <tr
                      key={`${prodIndex}-shirt-${quality}-${qualityIndex}`}
                      style={{
                        backgroundColor: prodIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                        borderBottom: '1px solid #ddd',
                      }}
                    >
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {group.quantity}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {formOptions[prodType]?.name ?? 'Shirt'}
                        {displayQuality ? ` (${displayQuality})` : ''}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                        {group.price.toFixed(2)}
                      </td>
                    </tr>
                  )
                })
              }
              
              // For non-shirt products or shirts without extra shirts, display normally
              let name
              let price = 0

              if (prodType === 'collection') {
                price = prod.orderCollection.price
                const productName = get(prod, `orderCollection.productName`, '')
                name = productName ? `Fixed SKU - ${productName}` : 'Fixed SKU'
              } else if (prodType === 'adjustment') {
                price = prod.orderAdjustment.price
                name = 'Adjustment'
              } else if (prod.orderProductType === 'fixed_design') {
                price = prod.orderFixedDesign.price
                const productName = get(prod, 'orderFixedDesign.productName.en', '') ||
                                   get(prod, 'orderFixedDesign.productName.zh', '') ||
                                   'Product'
                name = `Fixed Design - ${productName}`
              } else {
                price = prod.orderCustom.price
                // Extra shirts (for non-shirt products, just sum them)
                const extraShirts = get(
                  prod,
                  'orderCustom.orderCustomShirt.extraShirts',
                  []
                ) || []
                extraShirts.forEach((item) => {
                  price += get(item, 'priceNum', 0)
                })
                name = formOptions[prodType]?.name ?? 'Product'
              }

              // Calculate the quantity of additional shirts
              const quantity =
                (get(prod, 'orderCustom.orderCustomShirt.extraShirts', []) || []).length + 1

              return (
                <tr
                  key={prodIndex}
                  style={{
                    backgroundColor: prodIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                    borderBottom: '1px solid #ddd',
                  }}
                >
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {quantity}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {name}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                    {price.toFixed(2)}
                  </td>
                </tr>
              )
            })}
            {/* Total */}
            <tr>
              <td
                style={{
                  padding: '8px',
                  fontWeight: 'bold',
                  borderTop: '2px solid #000',
                }}
              ></td>
              <td
                style={{
                  padding: '8px',
                  fontWeight: 'bold',
                  borderTop: '2px solid #000',
                }}
              >
                Total:
              </td>
              <td
                style={{
                  padding: '8px',
                  fontWeight: 'bold',
                  borderTop: '2px solid #000',
                }}
              >
                {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                {(values.orderProducts || [])
                  .reduce((sum, prod) => {
                    const prodType = getFromKey(prod)
                    let price = 0

                    if (prodType === 'collection') {
                      price = prod.orderCollection.price
                    } else if (prodType === 'adjustment') {
                      price = prod.orderAdjustment.price
                    } else if (prod.orderProductType === 'fixed_design') {
                      price = prod.orderFixedDesign.price
                    } else {
                      price = prod.orderCustom.price
                      const extraShirts = get(
                        prod,
                        'orderCustom.orderCustomShirt.extraShirts',
                        []
                      ) || []
                      extraShirts.forEach((item) => {
                        price += get(item, 'priceNum', 0)
                      })
                    }

                    return sum + price
                  }, 0)
                  .toFixed(2)}
              </td>
            </tr>

            <tr>
              <td></td>
              <td style={{ padding: '8px', fontWeight: 'bold' }}>Discount:</td>
              <td style={{ padding: '8px' }}>
                {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                {values.discount?.toFixed(2) || '0.00'}
              </td>
            </tr>
            <tr>
              <td></td>
              <td style={{ padding: '8px', fontWeight: 'bold' }}>Deposit 1:</td>
              <td style={{ padding: '8px' }}>
                {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                {values.deposit?.toFixed(2) || '0.00'}
              </td>
            </tr>
            <tr>
              <td></td>
              <td className="text-strong" style={{ padding: '8px' }}>
                Deposit 2:
              </td>
              <td style={{ padding: '8px' }}>
                {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                {values.deposit2?.toFixed(2) || '0.00'}
              </td>
            </tr>
            <tr>
              <td></td>
              <td
                className="text-strong"
                style={{
                  padding: '8px',
                }}
              >
                Balance:
              </td>
              <td
                style={{
                  padding: '8px',
                }}
              >
                {values?.currency ? values.currency.toUpperCase() : ''}{' '}
                {values.balance?.toFixed(2) || '0.00'}
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      <FormContent
        values={values}
        errors={errors}
        path=""
        handleChange={handleChange}
        forms={formsPayment}
      />
    </Space>
  )
}

export default Step5
